/**
 * Interface pour le consentement aux cookies
 */
export interface CookieConsent {
  accepted: boolean;
  date: string;
  version: string;
}

/**
 * Vérifie si l'utilisateur a donné son consentement aux cookies
 * @returns {boolean} true si le consentement a été donné, false sinon
 */
export const hasCookieConsent = (): boolean => {
  try {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) return false;
    
    const parsedConsent = JSON.parse(consent) as CookieConsent;
    return parsedConsent.accepted;
  } catch (error) {
    console.error('Erreur lors de la vérification du consentement aux cookies:', error);
    return false;
  }
};

/**
 * Récupère les informations de consentement aux cookies
 * @returns {CookieConsent | null} Les informations de consentement ou null si aucun consentement n'a été donné
 */
export const getCookieConsent = (): CookieConsent | null => {
  try {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) return null;
    
    return JSON.parse(consent) as CookieConsent;
  } catch (error) {
    console.error('Erreur lors de la récupération du consentement aux cookies:', error);
    return null;
  }
};

/**
 * Définit le consentement aux cookies
 * @param {boolean} accepted true si l'utilisateur accepte les cookies, false sinon
 * @param {string} version version de la politique de cookies
 */
export const setCookieConsent = (accepted: boolean, version: string = '1.0'): void => {
  try {
    const consent: CookieConsent = {
      accepted,
      date: new Date().toISOString(),
      version
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
  } catch (error) {
    console.error('Erreur lors de la définition du consentement aux cookies:', error);
  }
};

/**
 * Supprime le consentement aux cookies
 */
export const removeCookieConsent = (): void => {
  try {
    localStorage.removeItem('cookieConsent');
  } catch (error) {
    console.error('Erreur lors de la suppression du consentement aux cookies:', error);
  }
};

/**
 * Définit un cookie si l'utilisateur a donné son consentement
 * @param {string} name Nom du cookie
 * @param {string} value Valeur du cookie
 * @param {number} days Durée de vie du cookie en jours
 * @returns {boolean} true si le cookie a été défini, false sinon
 */
export const setConditionalCookie = (name: string, value: string, days: number): boolean => {
  if (!hasCookieConsent()) return false;
  
  try {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
    return true;
  } catch (error) {
    console.error('Erreur lors de la définition du cookie:', error);
    return false;
  }
};

/**
 * Récupère la valeur d'un cookie
 * @param {string} name Nom du cookie
 * @returns {string | null} Valeur du cookie ou null si le cookie n'existe pas
 */
export const getCookie = (name: string): string | null => {
  try {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération du cookie:', error);
    return null;
  }
};

/**
 * Supprime un cookie
 * @param {string} name Nom du cookie
 */
export const removeCookie = (name: string): void => {
  try {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Strict`;
  } catch (error) {
    console.error('Erreur lors de la suppression du cookie:', error);
  }
};